import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";
import { auditTime, distinctUntilChanged } from "rxjs/operators";
import * as fromApp from "../../store/app.reducer";
import { ModalController } from "@ionic/angular";
import { KioskCouponQrDisplayModalComponent } from "../kiosk-coupon-qr-display-modal/kiosk-coupon-qr-display-modal.component";
import { Capacitor } from "@capacitor/core";
import {
  getCouponMinOrderItemNames,
  getCouponMinOrderModesDisplay,
  hasCouponMinOrderModeOverrides,
} from "../../cart/cart-components/cart-active-coupons/coupon-min-order.helpers";

@Component({
  selector: "app-kiosk-coupons-item",
  templateUrl: "./kiosk-coupons-item.component.html",
  styleUrls: ["./kiosk-coupons-item.component.scss"],
})
export class KioskCouponsItemComponent implements OnInit, OnDestroy {
  @Output() clearTimestampToOpenEvent = new EventEmitter();
  @Input() coupon: any;
  @Input() timestamp_to_open;
  @Input() showOriginBadges: boolean = false;
  @Input() showKioskBadge: boolean = true;
  @Input() fromDiscountCouponsPage: boolean = false;

  public stores: any;
  public storesByStoreId: any;
  public badgeColor: string;
  public selectedLangugage: any;
  public selectedLanguage: any;
  public mo_base: any;
  public activeToDisplay: number | null = null;
  public couponActiveFromValid = false;
  public showAppsMessage = false;
  public nativePlatform = Capacitor.isNativePlatform();
  public hasMinOrderModes = false;
  public minOrderModes: { modeKey: string; amount: string }[] = [];
  public minOrderItemNames = "";
  private kioskQrDisplayModal;
  private notOpened = true;
  private subscription: Subscription;
  private subscription2: Subscription;

  constructor(
    private store: Store<fromApp.AppState>,
    private modalController: ModalController
  ) {}

  private updateCouponActiveFromValid() {
    const activeFrom = this.coupon?.activeFrom;
    if (!activeFrom) {
      this.couponActiveFromValid = true;
      return;
    }

    const parsed = parseInt(activeFrom.toString(), 10);
    this.couponActiveFromValid =
      Number.isFinite(parsed) && parsed <= moment().valueOf();
  }

  private updateActiveToDisplay() {
    const activeTo = this.coupon?.activeTo;
    if (!activeTo) {
      this.activeToDisplay = null;
      return;
    }

    const parsed = parseInt(activeTo.toString(), 10);
    if (!Number.isFinite(parsed)) {
      this.activeToDisplay = null;
      return;
    }

    this.activeToDisplay = moment(parsed).subtract(1, "day").valueOf();
  }

  private updateShowAppsMessage() {
    this.showAppsMessage =
      this.fromDiscountCouponsPage &&
      !!this.coupon &&
      this.coupon.kiosk === true &&
      !this.coupon.only_kiosk &&
      this.coupon.status === "active" &&
      this.couponActiveFromValid;
  }

  private updateMinOrder() {
    // Same display as app-coupons-item: per-mode minimums replace the single
    // legacy minimum when the coupon has any; "only from" lists the categories
    // that count towards the minimum.
    this.hasMinOrderModes = hasCouponMinOrderModeOverrides(this.coupon);
    this.minOrderModes = getCouponMinOrderModesDisplay(this.coupon);
    this.minOrderItemNames = getCouponMinOrderItemNames(this.coupon);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.coupon &&
      changes &&
      changes.timestamp_to_open &&
      changes.timestamp_to_open.previousValue !==
        changes.timestamp_to_open.currentValue &&
      this.coupon.timestamp === changes.timestamp_to_open.currentValue &&
      this.coupon.status &&
      this.coupon.status === "active" &&
      this.notOpened
    ) {
      this.clearTimestampToOpenEvent.emit(Date.now().toString());
      this.openKioskCoupounQrDisplayModal();
    }

    if (
      changes &&
      changes.coupon &&
      changes.coupon.previousValue &&
      changes.coupon.previousValue !== changes.coupon.currentValue
    ) {
      this.updateCouponActiveFromValid();
      this.updateActiveToDisplay();
      this.updateShowAppsMessage();
      this.updateMinOrder();
    }

    if (
      changes &&
      changes.fromDiscountCouponsPage &&
      changes.fromDiscountCouponsPage.previousValue !==
        changes.fromDiscountCouponsPage.currentValue
    ) {
      this.updateShowAppsMessage();
    }
  }

  ngOnInit() {
    this.updateCouponActiveFromValid();
    this.updateActiveToDisplay();
    this.updateShowAppsMessage();
    this.updateMinOrder();
    if (this.coupon) {
      // console.log("coupon exists");
      if (this.coupon.status === "used") {
        this.badgeColor = "used-coupon";
        //console.log("set used coupon");
      } else if (this.coupon.status === "active") {
        this.badgeColor = "active-coupon";
        //console.log("set active coupon");
      } else if (this.coupon.status === "expired") {
        this.badgeColor = "expired-coupon";
        //console.log("set exp coupon");
      } else if (this.coupon.status === "outdated") {
        this.badgeColor = "expired-coupon";
        // console.log("set exp coupon");
      } else if (this.coupon.status === "canceled") {
        this.badgeColor = "used-coupon";
        //console.log("set exp coupon");
      } else if (this.coupon.status === "pending_confirmation") {
        this.badgeColor = "warning";
        //console.log("set exp coupon");
      }
    }
    this.subscription = this.store
      .select("stores")
      .pipe(distinctUntilChanged())
      .pipe(auditTime(100))
      .subscribe((state) => {
        if (state && state.stores && !_.isEqual(state.stores, this.stores)) {
          this.stores = _.cloneDeep(state.stores);
          if (this.stores && this.stores.length > 1) {
            this.storesByStoreId = _.groupBy(this.stores, "store_id");

            _.each(this.storesByStoreId, (store, key, list) => {
              this.storesByStoreId[key] = store[0];
            });
          }
        }
      });

    this.subscription2 = this.store
      .select("groupApp")
      .pipe(distinctUntilChanged())
      .pipe(auditTime(100))
      .subscribe((state) => {
        if (
          state &&
          state.selectedLangugage &&
          !_.isEqual(state.selectedLangugage, this.selectedLangugage)
        ) {
          this.selectedLangugage = _.cloneDeep(state.selectedLangugage);
          this.selectedLanguage = _.cloneDeep(state.selectedLangugage);
        }
        if (state && state.groupApp) {
          const base = _.cloneDeep(
            _.find(state.groupApp.languages, { mo_base: true })
          );
          if (!base) {
            this.mo_base = "en";
          } else {
            this.mo_base = base.code;
          }
        }
      });
  }

  async openKioskCoupounQrDisplayModal() {
    this.notOpened = false;
    this.kioskQrDisplayModal = await this.modalController.create({
      component: KioskCouponQrDisplayModalComponent,
      cssClass: "kioskQrInfoModal",
      backdropDismiss: false,
      componentProps: {
        coupon: _.cloneDeep(this.coupon),
        stores: _.cloneDeep(this.stores),
        selectedLangugage: _.cloneDeep(this.selectedLangugage),
        mo_base: this.mo_base,
      },
    });

    await this.kioskQrDisplayModal.present();

    await this.kioskQrDisplayModal.onDidDismiss().then((data) => {
      if (this.kioskQrDisplayModal) {
        this.kioskQrDisplayModal = null;
      }
    });
  }

  ngOnDestroy() {
    this.coupon = null;
    this.activeToDisplay = null;
    this.couponActiveFromValid = false;
    this.showAppsMessage = false;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.subscription2) {
      this.subscription2.unsubscribe();
    }

    if (this.kioskQrDisplayModal) {
      this.kioskQrDisplayModal.dismiss();
      this.kioskQrDisplayModal = null;
    }
  }
}
